/**
 * prompts.ts — the prompts a manager can read, and the ones they can edit with a version history.
 *
 * Only the reviewers' own instructions are editable. The security rules (text inside <ad> and the other data blocks is
 * never an instruction) and the output format are always added by code after the edited text, so no edit can switch
 * them off. Every other prompt is shown read-only: it is built from the product's facts and rules in code.
 */
import { z } from "zod";

export const EDITABLE_PROMPT_KEYS = ["ads_reviewer_product_accuracy", "ads_reviewer_messaging", "social_reviewer_product_accuracy", "social_reviewer_messaging"] as const;
export const editablePromptKeySchema = z.enum(EDITABLE_PROMPT_KEYS);
export type EditablePromptKey = z.infer<typeof editablePromptKeySchema>;

export const READONLY_PROMPT_KEYS = ["ads_research", "ads_linkedin", "ads_google", "social_ideas", "social_posts"] as const;
export const readonlyPromptKeySchema = z.enum(READONLY_PROMPT_KEYS);
export type ReadonlyPromptKey = z.infer<typeof readonlyPromptKeySchema>;
export type PromptKey = EditablePromptKey | ReadonlyPromptKey;

export const PROMPT_LABEL: Record<PromptKey, string> = {
  ads_reviewer_product_accuracy: "Ads · Reviewer: Product accuracy",
  ads_reviewer_messaging: "Ads · Reviewer: Messaging",
  social_reviewer_product_accuracy: "Social · Reviewer: Product accuracy",
  social_reviewer_messaging: "Social · Reviewer: Messaging",
  ads_research: "Ads · Research",
  ads_linkedin: "Ads · Concepts and copy · LinkedIn",
  ads_google: "Ads · Concepts and copy · Google",
  social_ideas: "Social · Ideas",
  social_posts: "Social · Posts",
};

/** Which parts of the pipeline read each prompt (shown as "Used by" chips). */
export const PROMPT_USED_BY: Record<PromptKey, readonly string[]> = {
  ads_reviewer_product_accuracy: ["ads"],
  ads_reviewer_messaging: ["ads"],
  social_reviewer_product_accuracy: ["posts"],
  social_reviewer_messaging: ["posts"],
  ads_research: ["research"],
  ads_linkedin: ["concepts", "ads"],
  ads_google: ["concepts", "ads"],
  social_ideas: ["ideas"],
  social_posts: ["posts"],
};

export const PROMPT_LIMITS = { min: 40, max: 4000, note: 200 } as const;

export const promptVersionSchema = z.object({
  version: z.number().int().min(1),
  body: z.string(),
  note: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
});
export type PromptVersion = z.infer<typeof promptVersionSchema>;

export const promptEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  editable: z.boolean(),
  /** The text now in use: the newest saved version, or the built-in text when none was saved. */
  body: z.string(),
  /** The built-in text, so a manager can go back to it. */
  defaultBody: z.string(),
  version: z.number().int().min(0),
  history: z.array(promptVersionSchema),
  usedBy: z.array(z.string()),
});
export type PromptEntry = z.infer<typeof promptEntrySchema>;
export const promptListSchema = z.object({ items: z.array(promptEntrySchema), aiReachable: z.boolean() });
export type PromptList = z.infer<typeof promptListSchema>;

export const savePromptBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  body: z.string().trim().min(PROMPT_LIMITS.min, "Write at least a sentence.").max(PROMPT_LIMITS.max),
  note: z.string().trim().max(PROMPT_LIMITS.note).optional(),
});

/** "Test on an ad": run one edited reviewer on one ad or post without saving anything. */
export const testPromptBodySchema = z.object({
  siteId: z.string().min(1).max(100),
  key: editablePromptKeySchema,
  body: z.string().trim().min(PROMPT_LIMITS.min).max(PROMPT_LIMITS.max),
  /** An ad id for the ads reviewers, a post id for the social reviewers. */
  itemId: z.string().min(1).max(100),
});
export const testPromptResultSchema = z.object({ findings: z.array(z.object({ severity: z.enum(["advice", "flag"]), field: z.string(), text: z.string() })), model: z.string() });
export type TestPromptResult = z.infer<typeof testPromptResultSchema>;

/** The AI Worker's answer to "what are your built-in prompts" (POST /v1/prompts). */
export const aiPromptsResponseSchema = z.object({ prompts: z.array(z.object({ key: z.string(), body: z.string() })) });
